import type {
  AssistantConversationSummary,
  AssistantMessage as HarnessAssistantMessage,
} from '@/apis/assistant'

import type { AssistantMessage, AssistantSession } from './assistant-models'
import { createOrderImportCardState } from './order-import-state'
import { createProductionHistoryCardState } from './production-history-state'

export function createAssistantSession(
  id = `session-${Date.now()}`
): AssistantSession {
  return {
    id,
    status: 'idle',
    title: '新对话',
    group: '今天',
    messages: [],
  }
}

/** 新会话唯一：复用尚未获得 Harness conversation_id 的本地会话。 */
export function findOrCreateAssistantSession(sessions: AssistantSession[]): {
  session: AssistantSession
  created: boolean
} {
  const session = sessions.find((item) => !item.conversationId)
  return session
    ? { session, created: false }
    : { session: createAssistantSession(), created: true }
}

function getAssistantConversationGroup(
  updatedAt: string
): AssistantSession['group'] {
  const updatedDate = new Date(updatedAt)
  if (Number.isNaN(updatedDate.getTime())) return '近一周'

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  updatedDate.setHours(0, 0, 0, 0)
  const dayDistance = Math.floor(
    (today.getTime() - updatedDate.getTime()) / (24 * 60 * 60 * 1000)
  )
  if (dayDistance <= 0) return '今天'
  if (dayDistance === 1) return '昨天'
  return '近一周'
}

/** 将 Harness 会话摘要转为可选中的本地会话，消息在用户点击后再加载。 */
export function createAssistantConversationSession(
  conversation: AssistantConversationSummary
): AssistantSession {
  return {
    id: `conversation-${conversation.conversationId}`,
    conversationId: conversation.conversationId,
    status: 'idle',
    title: conversation.title || '新对话',
    updatedAt: conversation.updatedAt,
    group: getAssistantConversationGroup(conversation.updatedAt),
    messages: [],
  }
}

function formatAssistantMessageTime(createdAt: string): string {
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return ''
  return `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`
}

/** 将 Harness 原始消息（含 include_tools 工具结果）恢复为会话 UI。 */
export function applyAssistantConversationMessages(
  session: AssistantSession,
  messages: HarnessAssistantMessage[]
): AssistantSession {
  return {
    ...session,
    status: 'idle',
    messages: messages.reduce<AssistantMessage[]>((result, message) => {
      if (message.role === 'tool') {
        try {
          const storedContent = JSON.parse(message.content) as {
            content?: unknown
            data?: unknown
          }
          const content =
            !storedContent.data &&
            storedContent.content &&
            typeof storedContent.content === 'object'
              ? storedContent.content
              : storedContent
          const toolResult = content as {
            data?: {
              type?: unknown
              records?: Array<{
                id: number
                name: string
                order_codes: string
                buyer_address?: string | null
                customer_name?: string | null
                production_line: string
                create_time: string
                download_url?: string
              }>
              total?: number
              returned?: number
              note?: string
              query?: Record<string, unknown>
              orders?: Array<{
                id: number
                order_code: string
                buyer_address: string
                customer_name: string
                create_time: string
                rooms: Array<{ id: number; name: string; create_time: string }>
              }>
            }
          }
          if (toolResult.data?.type === 'production-history') {
            const records = toolResult.data.records || []
            if (records.length === 0) {
              result.push({
                id: message.id,
                role: 'assistant',
                kind: 'text',
                time: formatAssistantMessageTime(message.createdAt),
                toolStatus: 'completed',
                text: '当前查询条件没有下载生产的记录',
              })
              return result
            }
            result.push({
              id: message.id,
              role: 'assistant',
              kind: 'production-history',
              time: formatAssistantMessageTime(message.createdAt),
              toolStatus: 'completed',
              productionHistory: createProductionHistoryCardState(),
              productionRecords: records.map((record) => ({
                id: String(record.id),
                name: record.name,
                orderNo: record.order_codes,
                address: record.buyer_address || '',
                customer: record.customer_name || '',
                line: record.production_line,
                completedAt: record.create_time,
                downloadUrl: record.download_url,
              })),
              productionSummary: {
                total: toolResult.data.total || 0,
                returned: toolResult.data.returned || 0,
                page: Number(toolResult.data.query?.page || 1),
                limit: Number(
                  toolResult.data.query?.limit || toolResult.data.returned || 1
                ),
                note: toolResult.data.note || '',
              },
              productionQuery: toolResult.data.query
                ? { query: toolResult.data.query }
                : undefined,
            })
          } else if (toolResult.data?.type === 'order-list') {
            const query = toolResult.data.query
            const orders = toolResult.data.orders || []
            if (orders.length === 0) {
              result.push({
                id: message.id,
                role: 'assistant',
                kind: 'text',
                time: formatAssistantMessageTime(message.createdAt),
                toolStatus: 'completed',
                text: '当前查询条件没有可导入的订单',
              })
              return result
            }
            const orderImportOrders = orders.map((order) => ({
              id: String(order.id),
              orderNo: order.order_code,
              address: order.buyer_address,
              customer: order.customer_name,
              createdAt: order.create_time,
              rooms: order.rooms.map((room) => ({
                id: String(room.id),
                name: room.name,
                createdAt: room.create_time,
              })),
            }))
            result.push({
              id: message.id,
              role: 'assistant',
              kind: 'order-import',
              time: formatAssistantMessageTime(message.createdAt),
              toolStatus: 'completed',
              orderImport: createOrderImportCardState(orderImportOrders),
              orderImportOrders,
              orderImportSummary: {
                total: toolResult.data.total || 0,
                page: Number(query?.page || 1),
                limit: Number(query?.limit || 20),
              },
              orderImportQuery: query ? { query } : undefined,
            })
          }
        } catch {
          // Tool 历史记录不属于可渲染卡片时不生成空白助手消息。
        }
        return result
      }
      const previous = result[result.length - 1]
      if (
        message.role === 'assistant' &&
        previous &&
        (previous.kind === 'order-import' ||
          previous.kind === 'production-history' ||
          previous.toolStatus)
      ) {
        return result
      }
      result.push({
        id: message.id,
        role: message.role === 'user' ? 'user' : 'assistant',
        kind: 'text',
        time: formatAssistantMessageTime(message.createdAt),
        text: message.content,
      })
      return result
    }, []),
  }
}
